import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerConfig, NgbDropdown, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { Moment } from 'moment';
import { FunctionsHelper } from 'src/app/helpers/functions';

export interface DatesRange {
  fromDate: NgbDate | null;
  toDate: NgbDate | null;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class DateRangePickerComponent implements OnInit {
  @Input() calendarRange!: DatesRange;
  @Output() datesChanged = new EventEmitter<DatesRange>();

  @ViewChild('dateRangeDropdown') dateRangeDropdown!: NgbDropdown;
  @ViewChild('dateRangePopover') dateRangePopover!: NgbPopover;

  dateRanges: any;
  selectedDateRangeIndex = 1;
  dateRangeToggleText = '';
  dateRangeDatesText = '';

  hoveredDate: NgbDate | null = null;
  fromDate!: NgbDate | null;
  toDate!: NgbDate | null;
  invalidCustomDates: boolean = false;

  constructor(
    private _functionsHelper: FunctionsHelper,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    private config: NgbDatepickerConfig
  ) { }

  ngOnInit(): void {
    if (!this.calendarRange) {
      this.calendarRange = {
        fromDate: this._functionsHelper.momentToNgbDate(moment().subtract(91, 'days')),
        toDate: this._functionsHelper.momentToNgbDate(moment().subtract(1, 'days')),
      };
    }

    this.dateRanges = [
      {
        name: 'Last 7 days',
        fromDate: moment().subtract(6, 'days'),
        toDate: moment(),
      },
      {
        name: 'Last 30 Days',
        fromDate: moment().subtract(29, 'days'),
        toDate: moment(),
      },
      {
        name: 'Last 45 Days',
        fromDate: moment().subtract(44, 'days'),
        toDate: moment(),
        // disabled: true,
      },
      {
        name: 'Last 60 Days',
        fromDate: moment().subtract(59, 'days'),
        toDate: moment(),
        // disabled: true,
      },
      {
        name: 'Last 90 Days',
        fromDate: moment().subtract(89, 'days'),
        toDate: moment(),
        // disabled: true,
      },
    ];

    this.setDateRangePickerToggleText();

    // this.maxMinDate = { year: this.lastDate.getFullYear(), month: this.lastDate.getMonth() - 2, day: this.lastDate.getDate() - 1 };

    this.emitDates();
  }

  isDateDisabled = (date: NgbDate) => {
    return date.before(this.calendarRange.fromDate) || date.after(this.calendarRange.toDate);
  };

  emitDates = () => {
    this.datesChanged.emit({
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
  };

  dateRangeClickChange(key: number) {
    if (key == -1 || this.selectedDateRangeIndex != key) {
      this.selectedDateRangeIndex = key;
      this.setDateRangePickerToggleText();
      this.emitDates();
    }
    this.dateRangePopover.close();
    this.dateRangeDropdown.close();
  }

  setDateRangePickerToggleText() {
    if (this.selectedDateRangeIndex >= 0) {
      this.dateRangeToggleText = this.dateRanges[this.selectedDateRangeIndex].name;
      const from = this.dateRanges[this.selectedDateRangeIndex].fromDate;
      const to = this.dateRanges[this.selectedDateRangeIndex].toDate;

      // update custom date range picker dates
      this.fromDate = this.calendar.getToday();
      this.fromDate.month = Number(from.format('M'));
      this.fromDate.day = Number(from.format('D'));
      this.fromDate.year = Number(from.format('YYYY'));

      this.toDate = this.calendar.getToday();
      this.toDate.month = Number(to.format('M'));
      this.toDate.day = Number(to.format('D'));
      this.toDate.year = Number(to.format('YYYY'));

      this.dateRangeDatesText = this.generateDatesText(from, to);
    } else {
      this.dateRangeToggleText = 'Custom';
      const from = this._functionsHelper.ngbDateToMoment(this.fromDate);
      const to = this._functionsHelper.ngbDateToMoment(this.toDate);
      this.dateRangeDatesText = this.generateDatesText(from, to);
    }
  }

  generateDatesText(fromDate: Moment | null, toDate: Moment | null): string {
    let output: any = '';
    const fromStr = fromDate?.format('MMM D, YYYY');
    const toStr = toDate?.format('MMM D, YYYY');
    if (fromStr === toStr) {
      output = fromStr;
    } else {
      output = fromStr + ' - ' + toStr;
    }
    return output;
  }

  dateRangeOpenChange(opened: boolean) {
    if (!opened) {
      this.dateRangePopover.close();
    }
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) &&
      date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) { return this.toDate && date.after(this.fromDate) && date.before(this.toDate); }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) ||
      this.isHovered(date);
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  customDateApplyClick() {
    if (this.fromDate !== undefined && this.toDate !== undefined && this.fromDate !== null && this.toDate !== null) {
      this.invalidCustomDates = false;
      this.dateRangeClickChange(-1);
    } else {
      this.invalidCustomDates = true;
    }
  }

}
